import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass';
import styled, { css } from 'styled-components';
import { maxWidth, minWidth } from 'styled-system';
import AriaModal from 'react-aria-modal';
import { Close } from '@oca/icons';
// eslint-disable-next-line import/no-cycle
import { Typography } from '../atoms';

const DrawerContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  pointer-events: none;
  overflow: hidden;
`;

const DialogWrapper = styled(Box)`
  position: relative;
  width: min(560px, 100vw);
  height: 100vh;
  max-height: 100vh;
  border-radius: 0;
  background: white;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  ${maxWidth};
  ${minWidth};
`;

const DialogHeader = styled(Flex)`
  padding: 20px 24px;
  border-bottom: 1px solid #f1f1f1;
  flex-shrink: 0;
`;

const DialogBody = styled.div`
  padding: 24px;
  text-align: left;
  flex: 1;
  overflow-y: auto;

  ${({ scrollBody }) =>
    scrollBody
      ? css`
          overflow-y: auto;
        `
      : css`
          overflow-y: visible;
        `}
`;

const ActionBtns = ({ onClickOnClose }) => {
  return (
    <Box ml={15}>
      <Box
        style={{ cursor: 'pointer' }}
        tabIndex="0"
        role="button"
        onClick={e => {
          e.stopPropagation();
          onClickOnClose();
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onClickOnClose();
          }
        }}
      >
        <Close />
      </Box>
    </Box>
  );
};

ActionBtns.propTypes = {
  onClickOnClose: PropTypes.func.isRequired,
};

export const Dialog = ({
  children,
  title,
  onClose,
  onClickOnClose,
  underlayClickExits,
  scrollBody,
  maxWidth: maxWidthProp,
  minWidth: minWidthProp,
  width,
  ...other
}) => {
  const handleClose = onClose || onClickOnClose;

  return (
    <AriaModal
      focusTrapPaused
      titleText={typeof title === 'string' ? title : 'Dialog'}
      onExit={handleClose}
      underlayClickExits={underlayClickExits}
      underlayStyle={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.35)',
        zIndex: 1000,
      }}
      {...other}
    >
      <DrawerContainer onClick={handleClose}>
        <DialogWrapper
          width={width || 'min(500px, 100vw)'}
          minWidth={minWidthProp}
          maxWidth={maxWidthProp || '500px'}
          onClick={e => e.stopPropagation()}
        >
          <DialogHeader
            alignItems="center"
            justifyContent="space-between"
            pl={24}
            pr={24}
          >
            <Typography variant="h2" lineHeight={1.1} noWrap>
              {title}
            </Typography>

            <ActionBtns onClickOnClose={handleClose} />
          </DialogHeader>

          <DialogBody scrollBody={scrollBody}>{children}</DialogBody>
        </DialogWrapper>
      </DrawerContainer>
    </AriaModal>
  );
};

Dialog.propTypes = {
  ...AriaModal.propTypes,
  scrollBody: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  onClose: PropTypes.func,
  onClickOnClose: PropTypes.func,
  children: PropTypes.node.isRequired,
  underlayClickExits: PropTypes.bool,
  maxWidth: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.number,
    PropTypes.string,
  ]),
  minWidth: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.number,
    PropTypes.string,
  ]),
  width: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.number,
    PropTypes.string,
  ]),
};

Dialog.defaultProps = {
  underlayClickExits: true,
  scrollBody: true,
  onClose: undefined,
  onClickOnClose: undefined,
  maxWidth: undefined,
  minWidth: undefined,
  width: '420px',
};
